import { ORIGIN_META, recentGenPriority } from './constants';

/**
 * Helper di formattazione e derivazione dati per la feature batch.
 * Nessuna logica di rete qui: solo trasformazioni pure e presentazionali.
 */

/** Converte una data ISO/serie in stringa locale leggibile; robusto ai null. */
export const formatDateTime = (value) => {
  if (!value) return '—';
  // Il backend può restituire sia ISO 8601 sia "YYYY-MM-DD HH:mm:ss.ssssss".
  const iso = typeof value === 'string' ? value.replace(' ', 'T') : value;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Millisecondi trascorsi tra due timestamp, come durata leggibile. */
export const formatDuration = (start, end) => {
  if (!start) return '—';
  const s = new Date(String(start).replace(' ', 'T')).getTime();
  const e = end ? new Date(String(end).replace(' ', 'T')).getTime() : Date.now();
  if (Number.isNaN(s) || Number.isNaN(e) || e < s) return '—';
  const secs = Math.round((e - s) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return `${mins}m ${rem}s`;
};

/** Dimensione in byte → stringa leggibile. */
export const formatBytes = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

/** Converte un valore data in stringa ISO UTC per i query param, o null. */
export const toIsoUtc = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

/**
 * Motivo della modifica: quali timestamp coincidono con `effectiveModifiedAt`.
 * Quando il timestamp coincide con una cancellazione CRON restituisce la
 * variante `*Deleted`, cosi la UI distingue una rimozione da un aggiornamento.
 */
export const deriveOrigins = (item) => {
  if (!item) return [];
  const eff = item.effectiveModifiedAt;
  if (!eff) return [];
  const sources = [
    { key: 'macro', modifiedAt: item.macroModifiedAt },
    {
      key: 'table',
      deletedKey: 'tableDeleted',
      modifiedAt: item.tableModifiedAt,
      deletedAt: item.tableDeletedAt,
    },
    {
      key: 'fields',
      deletedKey: 'fieldsDeleted',
      modifiedAt: item.fieldModifiedAt,
      deletedAt: item.fieldDeletedAt,
    },
    {
      key: 'operators',
      deletedKey: 'operatorsDeleted',
      modifiedAt: item.operatorModifiedAt,
      deletedAt: item.operatorDeletedAt,
    },
    {
      key: 'objects',
      deletedKey: 'objectsDeleted',
      modifiedAt: item.objectModifiedAt,
      deletedAt: item.objectDeletedAt,
    },
    {
      key: 'attributes',
      deletedKey: 'attributesDeleted',
      modifiedAt: item.attributeModifiedAt,
      deletedAt: item.attributeDeletedAt,
    },
  ];
  const origins = sources
    .filter(({ modifiedAt }) => modifiedAt && String(modifiedAt) === String(eff))
    .map(({ key, deletedKey, deletedAt }) => (
      deletedKey && deletedAt && String(deletedAt) === String(eff) ? deletedKey : key
    ));
  // Se nessuno coincide esattamente (arrotondamenti), ripiega su 'macro'.
  return origins.length > 0 ? origins : ['macro'];
};

export const originMeta = (key) => ORIGIN_META[key] || null;

/**
 * Deduplica visuale delle tabelle a partire da un elenco di macrofunzioni
 * (recent items o macroFunctions del piano). Ritorna
 *   [{ tableId, tableName, moduleId, macroFunctionIds: number[] }]
 */
export const dedupeTables = (macroItems = []) => {
  const map = new Map();
  for (const m of macroItems) {
    const tableId = m.tableId ?? m.table?.id;
    if (tableId == null) continue;
    const tableName = m.tableName ?? m.table?.name ?? `Tabella ${tableId}`;
    const moduleId = m.moduleId ?? m.module?.id ?? null;
    if (!map.has(tableId)) {
      map.set(tableId, { tableId, tableName, moduleId, macroFunctionIds: [] });
    }
    map.get(tableId).macroFunctionIds.push(m.id);
  }
  return Array.from(map.values());
};

/** Percentuale intera 0..100, protetta da divisione per zero. */
export const percent = (done, total) => {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.round((done / total) * 100));
};

/**
 * Task completati (qualsiasi stato terminale) da un riepilogo batch.
 */
export const completedFromSummary = (summary) => {
  if (!summary) return 0;
  return (
    (summary.success || 0) +
    (summary.failed || 0) +
    (summary.cancelled || 0) +
    (summary.interrupted || 0)
  );
};

/** Stato generale della generazione di un item /recent (safe). */
export const recentGenStatus = (item) => item?.generation?.status || null;

/**
 * Ordina i recent item: prima gli stati che richiedono attenzione
 * (NO_SUCCESS → CHANGED_AFTER_SUCCESS → SUCCESS_AFTER_CHANGE) e, a parità,
 * per modifica effettiva decrescente. Non muta l'array in ingresso.
 */
export const sortRecentItems = (items = []) => {
  const epoch = (it) => {
    if (typeof it.effectiveModifiedEpochMs === 'number') return it.effectiveModifiedEpochMs;
    const d = new Date(String(it.effectiveModifiedAt || '').replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  };
  return [...items].sort((a, b) => {
    const pa = recentGenPriority(recentGenStatus(a));
    const pb = recentGenPriority(recentGenStatus(b));
    if (pa !== pb) return pa - pb;
    return epoch(b) - epoch(a);
  });
};

/**
 * Rende leggibile un valore di changedFields (before/after).
 * - null / undefined / stringa vuota → "vuoto";
 * - booleani → "Sì" / "No";
 * - il resto → stringa. La troncatura è responsabilità della UI (title).
 */
export const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return { text: 'vuoto', isEmpty: true };
  }
  if (typeof value === 'boolean') {
    return { text: value ? 'Sì' : 'No', isEmpty: false };
  }
  return { text: String(value), isEmpty: false };
};

// Ordine di visualizzazione delle sorgenti nel dettaglio.
export const CHANGE_SOURCE_ORDER = [
  'macro',
  'table',
  'fields',
  'operators',
  'objects',
  'attributes',
];

/**
 * Netting del rumore da ri-censimento sugli attributi.
 *
 * Il censimento cancella FISICAMENTE tutti gli attributi di un oggetto e li
 * ricrea come nuove righe (nuovi ID): nel dettaglio ciò appare come DELETE (ID
 * vecchi) + CREATE (ID nuovi) dello stesso attributo. Poiché CREATE/DELETE non
 * portano i valori dei campi, l'unica chiave affidabile è il NOME entità.
 *
 * Regole per ciascun nome:
 * - UPDATE → sempre reale (valori effettivamente cambiati);
 * - CREATE e DELETE entrambi presenti → ricensimento (churn), non reale;
 * - solo CREATE → inserimento reale;
 * - solo DELETE → cancellazione reale.
 *
 * @returns {{ real: object[], churn: object[] }}
 */
export const reconcileAttributeItems = (attributeItems = []) => {
  const byName = new Map();
  for (const it of attributeItems) {
    const key = it.entityName || `#${it.entityId}`;
    if (!byName.has(key)) byName.set(key, []);
    byName.get(key).push(it);
  }
  const real = [];
  const churn = [];
  for (const group of byName.values()) {
    const updates = group.filter((g) => g.action === 'UPDATE');
    const createsDeletes = group.filter((g) => g.action !== 'UPDATE');
    real.push(...updates);
    const hasCreate = createsDeletes.some((g) => g.action === 'CREATE');
    const hasDelete = createsDeletes.some((g) => g.action === 'DELETE');
    if (hasCreate && hasDelete) {
      churn.push(...createsDeletes); // delete + ricreazione: rumore
    } else {
      real.push(...createsDeletes); // solo create o solo delete: reale
    }
  }
  return { real, churn };
};

/** Conteggi created/updated/deleted da una lista di item di dettaglio. */
export const summarizeChangeItems = (items = []) => {
  const totals = { total: items.length, created: 0, updated: 0, deleted: 0 };
  for (const it of items) {
    if (it.action === 'CREATE') totals.created += 1;
    else if (it.action === 'DELETE') totals.deleted += 1;
    else totals.updated += 1;
  }
  return totals;
};
